/**
 * Frontend scope permission helpers.
 *
 * SCOPE_ALIAS_MAP: maps each specific scope to parent/legacy scopes that also grant it.
 * This mirrors the backend's RequireOauthScope OR logic and supports legacy manage scopes.
 *
 * When adding a new module:
 *   1. Add scope_id entries to SCOPE_ALIAS_MAP for each action
 *   2. Include the .manage alias and system:users-management as fallbacks
 */

const SCOPE_ALIAS_MAP = {
  'dashboard.view': ['system:users-management'],

  // ── RMC2 domain ────────────────────────────────────────────────────────────
  // Mirrors the backend OauthPermissionCatalog: every action lists the .manage
  // parent plus system:users-management as fallbacks, matching RequireOauthScope's
  // OR logic so the SPA hides exactly what the API would refuse.
  'branches.view':              ['branches.manage', 'system:users-management'],
  'branches.create':            ['branches.manage', 'system:users-management'],
  'branches.update':            ['branches.manage', 'system:users-management'],
  'branches.delete':            ['branches.manage', 'system:users-management'],
  'branches.export':            ['branches.manage', 'system:users-management'],

  'members.view':               ['members.view-all', 'members.manage', 'system:users-management'],
  'members.view-all':           ['members.manage', 'system:users-management'],
  'members.create':             ['members.manage', 'system:users-management'],
  'members.update':             ['members.manage', 'system:users-management'],
  // Super Admin only — deliberately NO .manage fallback (spec §10).
  'members.transfer-upline':    ['system:users-management'],
  'members.reset-password':     ['members.manage', 'system:users-management'],
  // Self-service, so every member role holds it directly. The alias is here so
  // an admin surface gated on it still resolves through members.manage.
  'members.update-own':         ['members.manage', 'system:users-management'],
  'members.export':             ['members.manage', 'system:users-management'],

  'billing.view':               ['billing.view-all', 'billing.manage', 'system:users-management'],
  'billing.view-all':           ['billing.manage', 'system:users-management'],
  'billing.submit-proof':       [],
  'billing.verify':             ['billing.manage', 'system:users-management'],
  'billing.create':             ['billing.generate', 'billing.manage', 'system:users-management'],
  'billing.generate':           ['billing.manage', 'system:users-management'],
  'billing.export':             ['billing.manage', 'system:users-management'],

  'network.view':               ['network.view-all', 'network.manage', 'system:users-management'],
  'network.view-all':           ['network.manage', 'system:users-management'],
  'network.ledger.view':        ['network.view', 'network.manage', 'system:users-management'],

  'payouts.view':               ['payouts.view-all', 'payouts.manage', 'system:users-management'],
  'payouts.view-all':           ['payouts.manage', 'system:users-management'],
  'payouts.request':            [],
  'payouts.release':            ['payouts.manage', 'system:users-management'],
  'payouts.reject':             ['payouts.manage', 'system:users-management'],
  'bank-holidays.view':         ['payouts.manage', 'system:users-management'],
  'bank-holidays.create':       ['payouts.manage', 'system:users-management'],
  'bank-holidays.update':       ['payouts.manage', 'system:users-management'],
  'bank-holidays.delete':       ['payouts.manage', 'system:users-management'],

  'overrides.view':             ['overrides.manage', 'system:users-management'],
  'overrides.request-release':  ['overrides.manage', 'system:users-management'],
  // Deliberately NOT aliased to overrides.manage: releasing house money takes
  // a SECOND administrator, so the approver's scope is its own grant.
  'overrides.approve-release':  ['system:users-management'],

  'claims.view':                ['claims.view-all', 'claims.review', 'claims.manage', 'system:users-management'],
  'claims.view-all':            ['claims.manage', 'system:users-management'],
  'claims.file':                [],
  'claims.appeal':              [],
  'claims.review':              ['claims.manage', 'system:users-management'],
  'claims.approve':             ['claims.manage', 'system:users-management'],
  'claims.deny':                ['claims.manage', 'system:users-management'],
  'claims.disburse':            ['claims.manage', 'system:users-management'],
  'claims.export':              ['claims.manage', 'system:users-management'],
  'benefit-catalog.view':       ['claims.view', 'claims.file', 'claims.manage', 'system:users-management'],
  'benefit-catalog.manage':     ['claims.manage', 'system:users-management'],

  'support.view':               ['support.view-all', 'support.manage', 'system:users-management'],
  'support.view-all':           ['support.manage', 'system:users-management'],
  'support.create':             [],
  'support.reply':              ['support.manage', 'system:users-management'],
  'support.assign':             ['support.manage', 'system:users-management'],
  'support.close':              ['support.manage', 'system:users-management'],

  // The operator home screen. Deliberately NOT aliased to dashboard.view —
  // every signed-in role holds that, including members.
  'dashboard.operations':       ['reports.manage', 'system:users-management'],

  'reports.view':               ['reports.manage', 'system:users-management'],
  'reports.export':             ['reports.manage', 'system:users-management'],

  'oauth-users.view':               ['oauth-users.manage', 'system:users-management'],
  'oauth-users.create':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.update':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.delete':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.permissions.view':   ['oauth-users.manage', 'system:users-management', 'system:scope-management'],
  'oauth-users.permissions.update': ['oauth-users.manage', 'system:users-management', 'system:scope-management'],

  'oauth-clients.view':          ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.create':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.update':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.delete':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.rotate-secret': ['oauth-clients.manage', 'system:users-management'],

  'upload-s3-public-share-token.view':       ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.update':     ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.regenerate': ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.toggle':     ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.export':     ['upload-s3-public-share-token.manage', 'system:users-management'],

  'example-batch-upload.view':   ['example-batch-upload.manage', 'system:users-management'],
  'example-batch-upload.create': ['example-batch-upload.manage', 'system:users-management'],
  'example-batch-upload.update': ['example-batch-upload.manage', 'system:users-management'],
  'example-batch-upload.delete': ['example-batch-upload.manage', 'system:users-management'],


  // Voice + Video Calls (Architecture Rule 74)

  // ── CRM core (Rules 65 / 75 / 76) ───────────────────────────────────────────
  // The three visibility tiers are ADDITIVE: everyone who can read holds `.view`,
  // and `.view-team` / `.view-all` only WIDEN which rows come back. So `.view` is
  // the scope a nav item or a read button checks, and the wider tiers are listed
  // as aliases — a manager holding only `.view-all` still passes a `.view` gate.



  // Rule 77 — the AGENT side of the website widget. The visitor side is public
  // and carries no scope at all, so there is deliberately nothing here for it.


  // Reopening a closed deal rewrites a reported number — manager-only, so it
  // deliberately does NOT inherit from move-stage.





  'notifications.view': ['notifications.manage', 'system:users-management'],

  // Authorized Devices (Rule 73)
  'device-registration.view':   ['device-registration.delete', 'system:users-management'],
  'device-registration.delete': ['system:users-management'],

  // System Settings (Rule 74 — Phase 1 settings stack)
  'system-settings.view':   ['system-settings.manage', 'system:users-management'],
  'system-settings.update': ['system-settings.manage', 'system:users-management'],

  // System Administrator read-only viewers (Rule 68; Rule 65 system:* exception)
  'admin-audit-logs.view':          ['system:users-management'],
  'admin-audit-logs.export':        ['system:users-management'],
  'admin-outbound-http-logs.view':  ['system:users-management'],
  'admin-field-history.view':       ['system:users-management'],
  'admin-login-attempts.view':      ['system:users-management'],
  'admin-webhook-deliveries.view':  ['system:users-management'],
  'admin-failed-jobs.view':         ['system:users-management'],
  'admin-failed-jobs.retry':        ['system:users-management'],

  // ── Add new module scopes here ─────────────────────────────────────────────
  // Pattern:
  // '{module-key}.view':   ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.create': ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.update': ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.delete': ['{module-key}.manage', 'system:users-management'],
};

/**
 * Returns all scope IDs that grant a given required scope
 * (the scope itself plus all its aliases).
 */
function grantedScopesFor(requiredScope) {
  return [requiredScope, ...(SCOPE_ALIAS_MAP[requiredScope] ?? [])];
}

/**
 * Normalize a raw scope value from the API to a plain string array.
 */
export function normalizeScopeList(scopes) {
  return Array.isArray(scopes) ? scopes : [];
}

/**
 * Returns true if userScopes satisfies requiredScope (including aliases).
 */
export function hasScope(userScopes, requiredScope) {
  return grantedScopesFor(requiredScope).some((s) => userScopes.includes(s));
}

/**
 * Returns true if userScopes satisfies ANY scope in requiredScopes (OR logic).
 */
export function hasAnyScope(userScopes, requiredScopes) {
  return requiredScopes.some((s) => hasScope(userScopes, s));
}
